/**
 * Pricing configuration — tiers + the static feature-matrix fallback.
 *
 * Split out of `landingData.ts` (#8046): that module imports 8 design-system
 * icon components for `getFeatures()`, so anything importing the pricing
 * config from it dragged ~1–2 KB gz of icons + testimonials into the
 * `/pricing` chunk (pricing spec rev 2 §8.9). This module is **icon-free** so
 * `PricingPage` / `PricingSection` can import the tiers + matrix without that
 * leak.
 *
 * The live matrix (seeded from `prisma/seeds/seed-pricing-matrix.ts`, tiers
 * derived from `FEATURE_GATES`) is the source of truth in normal operation;
 * `pricingFeatures` below is the static fallback rendered only when
 * `GET /api/features/public/pricing-matrix` is unavailable.
 *
 * Pricing source of truth: `server/config/stripe.ts`
 * — MONTHLY_PRICE: 499 ($4.99/month)
 * — ANNUAL_PRICE:  4999 ($49.99/year, ~$4.16/month)
 */
import { isPublicLaunch } from './launch';

const _launch = isPublicLaunch();

// ── Price + trial source of truth (mirror of server/config/stripe.ts) ──
// These MUST match `server/config/stripe.ts`: MONTHLY_PRICE=499,
// ANNUAL_PRICE=4999, TRIAL_PERIOD_DAYS=14. Kept as cents (the Stripe unit) so
// the display strings below are derived, never hand-typed — that drift is what
// #15070/#15073 fixed. Pinned by pricingData.test.ts against those exact values.
/** KinDash Plus monthly price, in cents. Mirrors stripe.ts `MONTHLY_PRICE`. */
export const PLUS_MONTHLY_PRICE_CENTS = 499;
/** KinDash Plus annual price, in cents. Mirrors stripe.ts `ANNUAL_PRICE`. */
export const PLUS_ANNUAL_PRICE_CENTS = 4999;
/** Reverse-trial length, in days. Mirrors stripe.ts `TRIAL_PERIOD_DAYS`. */
export const TRIAL_PERIOD_DAYS = 14;

/** Which billing cadence the pricing surfaces are showing. Annual is default
 *  (framework §1.3: "annual is the default-selected toggle"). */
export type BillingPeriod = 'monthly' | 'annual';

/** Format a cents amount as a USD string, dropping the decimals only when the
 *  value is a whole number of dollars (so `$49.99` stays two-decimal). */
function formatUsd(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

/** Per-period Plus price display, derived from the cent constants above.
 *  `annual.perMonthNote` floors to match the "≈ $4.16/mo" framing already in
 *  this file's header — never a percentage ("2 months free", framework §1.3). */
export const plusPricing: Record<
  BillingPeriod,
  { amount: string; period: string; perMonthNote?: string }
> = {
  monthly: {
    amount: formatUsd(PLUS_MONTHLY_PRICE_CENTS), // "$4.99"
    period: '/month',
  },
  annual: {
    amount: formatUsd(PLUS_ANNUAL_PRICE_CENTS), // "$49.99"
    period: '/year',
    perMonthNote: `${formatUsd(Math.floor(PLUS_ANNUAL_PRICE_CENTS / 12))}/mo, billed yearly`, // "$4.16/mo, billed yearly"
  },
};

export interface PricingTier {
  /** SubscriptionTier enum key — drives testids and feature-label lookups. */
  tier: 'FREE' | 'PLUS' | 'PREMIUM';
  name: string;
  price?: string;
  period?: string;
  description: string;
  highlighted?: boolean;
  ctaText: string;
  ctaAction?: 'waitlist' | 'navigate';
  badge?: string;
}

/**
 * One row in the side-by-side feature matrix shown on the pricing
 * section. `values` maps a tier's `name` to either a string (e.g.
 * "Up to 5") or a boolean for ✓/—. The matrix renderer tolerates new
 * tiers being added to `pricingTiers` — features just need to add a
 * value for the new tier name.
 */
export interface PricingFeature {
  id: string;
  label: string;
  /** Optional one-line clarifier shown muted below the label. */
  hint?: string;
  /** Loose grouping used as a sub-header in the matrix. */
  category: string;
  values: Record<string /* tier name */, boolean | string>;
}

// Static fallback only — rendered when GET /api/features/public/pricing-matrix
// is unavailable. The live matrix (seeded from prisma/seeds/seed-pricing-matrix.ts,
// tiers derived from FEATURE_GATES) is the source of truth in normal operation.
// Premium values mirror the gate story: Premium ⊇ Plus, plus Premium-only
// wellbeing/insights rows. Higher tiers inherit lower-tier values.
export const pricingFeatures: PricingFeature[] = [
  // ── Core: in every tier ──────────────────────────────────────────
  {
    id: 'members',
    label: 'Family members',
    hint: 'Up to 10 members in all plans',
    category: 'Core',
    values: { Free: 'Up to 10', Plus: 'Up to 10', Premium: 'Up to 10' },
  },
  {
    id: 'tasks',
    label: 'Tasks & chores',
    hint: 'Per-kid lists, points, family progress',
    category: 'Core',
    values: { Free: true, Plus: true, Premium: true },
  },
  {
    // #15123 / PRICING-FRAMEWORK §2 rule 2 (D2): basic recurrence is FREE —
    // chores are inherently recurring. Templates, dependencies, projects and
    // smart scheduling remain Plus, and are not advertised as rows here.
    id: 'recurring-tasks',
    label: 'Recurring tasks & chore rotations',
    hint: 'Set a chore to repeat — daily, weekly, or on a rotation',
    category: 'Core',
    values: { Free: true, Plus: true, Premium: true },
  },
  {
    id: 'calendar',
    label: 'Shared family calendar',
    hint: 'Color-coded per person, two-household ready',
    category: 'Core',
    values: { Free: true, Plus: true, Premium: true },
  },
  {
    id: 'shopping',
    label: 'Shopping lists',
    hint: 'Categorized, multi-shopper, real-time sync',
    category: 'Core',
    values: { Free: true, Plus: true, Premium: true },
  },
  {
    id: 'mobile',
    label: 'Mobile app access',
    category: 'Core',
    values: { Free: true, Plus: true, Premium: true },
  },
  {
    id: 'notifications',
    label: 'Notifications',
    category: 'Core',
    values: { Free: 'Basic', Plus: 'Priority + advanced', Premium: 'Priority + advanced' },
  },

  // ── Money & meals ────────────────────────────────────────────────
  // Spend/save/give jars are FREE (FINANCIAL_LEDGER gate). Only automated
  // allowance (FAMILY_ALLOWANCES gate) requires Plus or above.
  {
    id: 'financial-ledger',
    label: 'Spend/save/give jars for kids',
    hint: 'Kid-facing money buckets for learning to manage money',
    category: 'Money & meals',
    values: { Free: true, Plus: true, Premium: true },
  },
  {
    id: 'allowance',
    label: 'Automated allowance',
    hint: 'Recurring allowances auto-deposited into kid money buckets',
    category: 'Money & meals',
    values: { Free: false, Plus: true, Premium: true },
  },
  {
    id: 'meals',
    label: 'Meal planning',
    hint: 'Drag recipes onto days, ingredients flow into the shopping list',
    category: 'Money & meals',
    values: { Free: false, Plus: true, Premium: true },
  },

  // ── Rewards: Free points, Plus XP/streaks ────────────────────────
  // #15123 / PRICING-FRAMEWORK §2 rule 3 (D3). Both rows are listed so the
  // Plus half reads as a visible, calm lock rather than an absence — kids can
  // only ask for what they can see.
  {
    id: 'points',
    label: 'Points for completed chores',
    hint: 'Kids earn points every time they finish a chore',
    category: 'Rewards',
    values: { Free: true, Plus: true, Premium: true },
  },
  {
    id: 'gamification',
    label: 'XP levels & streaks',
    hint: 'Levels to climb and streaks to keep',
    category: 'Rewards',
    values: { Free: false, Plus: true, Premium: true },
  },

  // ── Wellbeing & growth: Premium only ─────────────────────────────
  {
    id: 'mood',
    label: 'Mood & wellbeing check-ins',
    hint: 'Gentle, age-appropriate emotional check-ins for kids',
    category: 'Wellbeing & growth',
    values: { Free: false, Plus: false, Premium: true },
  },
  {
    // #8047 — single ADVANCED_ANALYTICS row. The bare 'analytics' row was a
    // duplicate of this one (both Premium-only, same gate); its label didn't
    // match the gate's landingPageLabel ("Advanced family insights & trends",
    // see seed-pricing-matrix.ts), so it was removed to match the live matrix.
    id: 'insights',
    label: 'Advanced family insights & trends',
    hint: 'Chore, habit, and wellbeing trends over time',
    category: 'Wellbeing & growth',
    values: { Free: false, Plus: false, Premium: true },
  },
];

export const pricingTiers: PricingTier[] = [
  {
    tier: 'FREE',
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Everything your family needs to get organized',
    ctaText: _launch ? 'Get started free' : 'Join the Waitlist',
    ctaAction: _launch ? 'navigate' : 'waitlist',
  },
  {
    tier: 'PLUS',
    name: 'Plus',
    // The Plus price displayed on the pricing surfaces is driven by the
    // monthly/annual toggle (see `plusPricing` above) — PricingSection /
    // PricingPage read `plusPricing[billingPeriod]` for this tier. These base
    // fields remain the monthly fallback for any consumer that renders a tier
    // card without the toggle.
    price: plusPricing.monthly.amount, // "$4.99"
    period: plusPricing.monthly.period, // "/month"
    description: 'For families who want the full picture',
    highlighted: true,
    ctaText: _launch ? 'Start with Plus' : 'Join the Waitlist',
    ctaAction: _launch ? 'navigate' : 'waitlist',
    // "Preview" communicates pre-launch without the implicit urgency of "Coming Soon"
    // (ND design principle: no urgency/scarcity copy on public surfaces).
    badge: _launch ? undefined : 'Preview',
  },
  {
    tier: 'PREMIUM',
    name: 'Premium',
    // No confirmed Stripe price yet — PricingSection.tsx renders "Pricing at
    // launch" when price/period are absent, which is the correct pre-launch state.
    // Premium is child-centric (wellbeing + insights); adult/household finance
    // is out of scope per CLAUDE.md.
    description: "For families who want the most support for kids' wellbeing and growth",
    ctaText: 'Join the Waitlist',
    ctaAction: 'waitlist',
    badge: _launch ? undefined : 'Preview',
  },
];
